import { useEffect, useMemo, useState } from "react";

import {
  TextField,
  Checkbox,
  Select,
  TableMetadata,
  useTable,
  Label,
  ArrowButton,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  createInitialInstitutionIds,
  getInitialTableState,
  createInitialSchoolCandidateInstitutionAdminSearch,
} from "./utils";
import {
  getSchoolCandidateInstitutionAdminFilteredList,
  getSchoolCandidateInstitutionAdminListExport,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  SearchCard,
  InstitutionSelect,
  ListTable,
} from "../../components";
import { useInput, useFilterPersistence } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import { Role } from "../../models";
import {
  SchoolCandidateInstitutionAdminSearch,
  SchoolCandidateInstitutionAdmin,
  SchoolCandidateStatus,
} from "../../models/SchoolCandidate";
import { SchoolCandidateInstitutionAdminSort } from "../../models/SchoolCandidate/SchoolCandidateInstitutionAdminSort";
import { ClaimType } from "../../models/User";
import {
  schoolCandidateDetailUrl,
  schoolCandidateInstitutionAdminDetailUrl,
} from "../../routes/routes";
import { setRedirectURL } from "../../slices/applicationSlice";
import { FormatUtils, TableUtils } from "../../utils";

//STORAGE KEY
const STORAGE_KEY = `SchoolCandidateInstitutionAdminSearchTableState`;

export function SchoolCandidateInstitutionAdminList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { schoolCandidateStatusCodeBook, requestTypeCodeBook } =
    useEnumCodeBooks();

  //INIT TABLE STATE WITH FILTERS
  const initialTableState = useMemo(
    () => getInitialTableState(STORAGE_KEY),
    []
  );

  const defaultInstitutionIds = useMemo(
    () => createInitialInstitutionIds(auth.currentRole),
    [auth.currentRole]
  );

  //INIT SEARCH FILTER
  const initialSchoolCandidateInstitutionAdminSearch = useMemo(
    () =>
      createInitialSchoolCandidateInstitutionAdminSearch({
        initialTableState,
        defaultInstitutionIds,
      }),
    [initialTableState, defaultInstitutionIds]
  );

  const isFounderRole = auth.currentRole?.role === Role.Founder;

  //TABLE DATA
  const defaultTableData = useMemo(
    () => TableUtils.tableDefaultData<SchoolCandidateInstitutionAdmin>(),
    []
  );
  const [tableData, setTableData] =
    useState<PagedResult<SchoolCandidateInstitutionAdmin>>(defaultTableData);

  //SET SEARCH FILTERS USING FILTER PERSISTENCE
  const {
    filterState: schoolCandidateInstitutionAdminSearch,
    setFilterState: setSchoolCandidateInstitutionAdminSearch,
    saveFilters,
    clearFilters,
    filterChanged,
  } = useFilterPersistence<SchoolCandidateInstitutionAdminSearch>({
    storageKey: `SchoolCandidateInstitutionAdminSearch`,
    initialState: initialSchoolCandidateInstitutionAdminSearch,
  });

  const [tableSearch, setTableSearch] =
    useState<SchoolCandidateInstitutionAdminSearch>(
      schoolCandidateInstitutionAdminSearch
    );

  //TABLE METADATA
  const tableMetadata: TableMetadata<SchoolCandidateInstitutionAdmin> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "child",
        header: t("common.schoolCandidateModel.childData"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName} &nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.oib}
              </div>
            </div>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.Child,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div className={"text-nowrap text-right"}>
              {hrDate(new Date(item.dateOfBirth))}
            </div>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.DateOfBirth,
      },
      {
        columnKey: "permanentAndTemporaryAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.residence?.permanentAddress?.street}&nbsp;
                  {item.residence?.permanentAddress?.houseNumber},&nbsp;
                  {item.residence?.permanentAddress?.town}
                </b>
              </div>
              {item.residence?.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.residence?.temporaryAddress.street}&nbsp;
                  {item.residence?.temporaryAddress.houseNumber},&nbsp;
                  {item.residence?.temporaryAddress.town}
                </div>
              )}
            </>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.Address,
      },
      {
        columnKey: "delayTemporaryExemption",
        header:
          t("common.schoolCandidateModel.delay") +
          "/" +
          t("common.schoolCandidateModel.temporaryExemptionForPreviousYear"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div>{item.postponement ? t("common.yes") : t("common.no")}</div>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.DelayTemporaryExemption,
      },
      {
        columnKey: "nationalMinoritySchool",
        header: t("common.schoolCandidateModel.nationalMinoritySchool"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          return (
            <div className={"text-nowrap"}>
              {item.religiousOrMinorityInstitutionName}
            </div>
          );
        },
        sortBy:
          SchoolCandidateInstitutionAdminSort.MinorityOrReligiousInstitution,
      },
      {
        columnKey: "status",
        header: t("common.schoolCandidateModel.schoolCandidateStatus"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          const status = schoolCandidateStatusCodeBook.find(
            (v) => v.id === item.status
          );
          return (
            <Label
              color={FormatUtils.getSchoolCandidateStatusColor(status!.id)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {status?.name}
            </Label>
          );
        },
        sortBy: SchoolCandidateInstitutionAdminSort.Status,
      },
      {
        columnKey: "requestType",
        header: t("common.schoolCandidateModel.requestType"),
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          if (item.requestType !== null) {
            const requestType = requestTypeCodeBook.find(
              (v) => v.id === item.requestType
            );
            return (
              <Label
                color={FormatUtils.getSchoolCandidateStatusColor(
                  requestType!.id
                )}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                {requestType!.name}
              </Label>
            );
          } else {
            return (
              <Label
                color={FormatUtils.getSchoolCandidateStatusColor()}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                Nema zahtjeva
              </Label>
            );
          }
        },
        sortBy: SchoolCandidateInstitutionAdminSort.RequestType,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: SchoolCandidateInstitutionAdmin) => {
          if (isFounderRole) return null;

          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] =
    useTable<SchoolCandidateInstitutionAdminSearch>({
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: tableData!.rowCount,
      defaultSort: {
        order: initialTableState.order,
        sortBy: initialTableState.sortBy,
      },
    });

  console.log("tableState", tableState);

  useEffect(() => {
    // Initial search
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const request =
        TableUtils.GetSearchRequest<SchoolCandidateInstitutionAdminSearch>(
          tableSearch,
          tableState
        );
      const itemList = await getSchoolCandidateInstitutionAdminFilteredList(
        request
      );
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: string) => {
    dispatch(setRedirectURL(history.location.pathname));

    if (!id) return;

    TableUtils.SetSessionStorageItem<SchoolCandidateInstitutionAdminSearch>(
      STORAGE_KEY,
      tableSearch,
      tableState
    );

    const detailId = Number(id.split("-")[0]);
    const detailUrlFunc =
      auth.currentRole?.role &&
      [Role.CountyAdministrator, Role.CountyLeader].includes(
        auth.currentRole?.role
      )
        ? schoolCandidateDetailUrl
        : schoolCandidateInstitutionAdminDetailUrl;

    history.push(detailUrlFunc(detailId));
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getSchoolCandidateInstitutionAdminListExport(
      schoolCandidateInstitutionAdminSearch,
      exportType
    );
  };

  const handleInstitutionChange = (id: number) => {
    const newSchoolCandidateInstitutionAdminSearch: SchoolCandidateInstitutionAdminSearch =
      {
        ...schoolCandidateInstitutionAdminSearch,
        institutionIds: !isNaN(id) ? [id] : [],
      };
    setSchoolCandidateInstitutionAdminSearch(
      newSchoolCandidateInstitutionAdminSearch
    );
  };

  return (
    <ContentArea>
      <ToolbarTop pageTitle={t("navigation.schoolCandidateList")} />
      <SearchCard
        onClickSearch={() => {
          setTableSearch(schoolCandidateInstitutionAdminSearch);
          saveFilters(schoolCandidateInstitutionAdminSearch);
          search();
        }}
        onClickClearSearch={() => {
          const defaultSearch: SchoolCandidateInstitutionAdminSearch = {
            institutionIds: defaultInstitutionIds,
            onlyWithAddReasonDelayPreviousYear: false,
            onlyWithTemporaryAddress: false,
            requestNotSubmitted: false,
          };

          clearFilters();
          setSchoolCandidateInstitutionAdminSearch(defaultSearch);
          setTableSearch(defaultSearch);
          search();
        }}
        isFilterChanged={filterChanged}
        hideDivider
      >
        <Row>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "institutionIds"
              )}
              value={
                schoolCandidateInstitutionAdminSearch.institutionIds &&
                schoolCandidateInstitutionAdminSearch.institutionIds.length > 0
                  ? schoolCandidateInstitutionAdminSearch.institutionIds[0]
                  : undefined
              }
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              showUser
            />
          </Col>
          <Col md={2} />
          <Col md={4}>
            <Select
              label={t("common.schoolCandidateModel.requestType")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "requestType"
              )}
              value={schoolCandidateInstitutionAdminSearch.requestType}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
              selectData={codeBookToSelectData(requestTypeCodeBook)}
              emptyOption={{ value: undefined, text: t("common.all") }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.childLastName")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "childLastName"
              )}
              value={schoolCandidateInstitutionAdminSearch.childLastName}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.childFirstName")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "childFirstName"
              )}
              value={schoolCandidateInstitutionAdminSearch.childFirstName}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.childOib")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "childOib"
              )}
              value={schoolCandidateInstitutionAdminSearch.childOib}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={3}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateStatus")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "status"
              )}
              value={schoolCandidateInstitutionAdminSearch.status}
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
              selectData={codeBookToSelectData(
                schoolCandidateStatusCodeBook.filter(
                  (status) =>
                    status.id !== SchoolCandidateStatus.notSent.valueOf()
                )
              )}
              emptyOption={{ value: undefined, text: t("common.all") }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t(
                "common.schoolCandidateModel.onlyWithAddReasonDelayPreviousYear"
              )}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "onlyWithAddReasonDelayPreviousYear"
              )}
              checked={
                schoolCandidateInstitutionAdminSearch.onlyWithAddReasonDelayPreviousYear
              }
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.onlyWithTemporaryAddress")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "onlyWithTemporaryAddress"
              )}
              checked={
                schoolCandidateInstitutionAdminSearch.onlyWithTemporaryAddress
              }
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.requestNotSubmitted")}
              name={nameof<typeof schoolCandidateInstitutionAdminSearch>(
                "requestNotSubmitted"
              )}
              checked={
                schoolCandidateInstitutionAdminSearch.requestNotSubmitted
              }
              onChange={handleChange(setSchoolCandidateInstitutionAdminSearch)}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload={!isFounderRole}
      />
    </ContentArea>
  );
}
